import React from 'react';
import { FormControl, Select, MenuItem } from '@mui/material';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import './MultiCategoryFilter.css';
import FilterRotator from './FilterRotator';

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface FilterConfig {
  id: string;
  label: string;
  options: FilterOption[];
  selectedOptions: string[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>;
}

interface MultiCategoryFilterProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  activeFilter: string;
  setActiveFilter: (id: string) => void;
  filters: FilterConfig[];
  label?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  showSearch?: boolean;
  onFilterChange?: () => void;
}

const MultiCategoryFilter: React.FC<MultiCategoryFilterProps> = ({
  isOpen,
  setIsOpen,
  activeFilter,
  setActiveFilter,
  filters,
  label = "Filters",
  searchValue = "",
  onSearchChange,
  showSearch = true,
  onFilterChange,
}) => {
  const handleOptionChange = (filterId: string, optionId: string, isChecked: boolean) => {
    const filter = filters.find(f => f.id === filterId);
    if (!filter) return;

    filter.setSelectedOptions(prev =>
      isChecked
        ? [...prev, optionId]
        : prev.filter(id => id !== optionId)
    );

    onFilterChange?.();
  };

  const handleClearAll = () => {
    filters.forEach(filter => {
      filter.setSelectedOptions([]);
    });
    if (onSearchChange) {
      onSearchChange("");
    }
    onFilterChange?.();
  };

  const handleFilterTypeChange = (filterId: string) => {
    if (onSearchChange) {
      onSearchChange("");
    }
    setActiveFilter(filterId);
  };

  const currentFilter = filters.find(f => f.id === activeFilter) || filters[0];
  const hasSelections = filters.some(f => f.selectedOptions.length > 0) || (searchValue?.length > 0);

  return (
    <FormControl className="manage_secondary_orders_filter">
      <span style={{ fontSize: "14px", color: "#6E6969" }}>Filter:</span>
      <Select
        variant="outlined"
        labelId="dropdown_label"
        open={isOpen}
        value={label}
        onOpen={() => {
          setIsOpen(true);
        }}
        onClose={() => setIsOpen(false)}
        renderValue={() =>
          <FilterRotator
            items={filters.map((f) => f.label)}
            interval={3000}
            style={{ fontSize: "1em", fontWeight: 400 }}
            labelClassName="manage_secondary_orders_filter-labelV2"
            hasSelected={false}
          />
        }
        sx={{
          border: hasSelections
            ? "1px solid #237C9D"
            : "1px solid #ccc",
          boxSizing: "border-box",
          height: "37px",
          minWidth: "190px",
          marginLeft: "10px",
          "& .MuiSelect-select": {
            height: "32px",
            padding: "6px 12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#909090",
            outline: "none",
            border: "none",
          },
          "& .MuiOutlinedInput-root.Mui-focused": {
            outline: "none",
            boxShadow: "none",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#DEDEDE",
            borderWidth: "1px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#DEDEDE",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#DEDEDE",
            borderWidth: "1px",
          },
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          PaperProps: {
            sx: {
              boxShadow: "none",
              marginTop: "4px",
              border: "1px solid #DEDEDE",
            },
          },
          MenuListProps: {
            sx: {
              paddingTop: "8px",
              paddingBottom: "0",
            },
          },
        }}
      >
        <MenuItem
          key={`filter-${activeFilter}`}
          disableRipple
          style={{
            padding: 0,
            backgroundColor: "transparent",
            outline: "none",
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div
            className="MSO_filter-dropdown-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="MSO_filter-header">
              <span className="MSO_filter-name">All Filters</span>
              <span
                className={`MSO_filter-clear-all ${(filters.some(f => f.selectedOptions.length > 0) || searchValue) &&
                  `MSO_filter-clear-all_active`
                  }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll();
                }}
              >
                Clear All
              </span>
            </div>

            {showSearch && (
              <div className="MSO_filter_searchBar_container">
                <input
                  className="MSO_fiter_searchBar"
                  placeholder="Search..."
                  value={searchValue}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  autoFocus
                />
              </div>
            )}

            <div className="MSO_filter-options-multiSelect">
              <div className="MSO_filter-options-left">
                {filters.map((filter) => (
                  <div
                    key={filter.id}
                    className={`${activeFilter === filter.id ? `MSO_filterSelected` : ''}`}
                    onClick={(e) => {
                      handleFilterTypeChange(filter.id);
                      e.stopPropagation();
                    }}
                  >
                    {filter.label} ({(filter.selectedOptions || []).length})
                  </div>
                ))}
              </div>
              <div className="MSO_filter-options-right">
                <SimpleBar className="MSO_filter-option-secondary">
                  {currentFilter.options
                    .filter(option => {
                      if (!searchValue) return true;
                      const searchTerm = searchValue.toLowerCase().replace(/\s+/g, '');
                      const optionText = option.label.toLowerCase().replace(/\s+/g, '');

                      return optionText.includes(searchTerm);
                    })
                    .map((option) => (
                      <label
                        key={option.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#444444",
                          fontSize: "13px",
                          cursor: "pointer",
                          width: "215px",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={(currentFilter.selectedOptions || []).includes(option.id)}
                          onChange={(e) =>
                            handleOptionChange(currentFilter.id, option.id, e.target.checked)
                          }
                          style={{
                            borderColor: "#CBCBCB",
                            cursor: "pointer",
                            height: "16px",
                            width: "14px"
                          }}
                        />
                        <span
                          title={option.label}
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}
                        >
                          {`${option.label}`}
                        </span>
                        {`(${option.count})`}
                      </label>
                    ))}
                </SimpleBar>
              </div>
            </div>
          </div>
        </MenuItem>
      </Select>
    </FormControl>
  );
};

export default MultiCategoryFilter;